import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import useHeaderHeight from '../../hooks/useHeaderHeight';
import { sendEmployeeMagicLink, submitEmployeeMagicLinkCode } from '../../redux/middleware/users';
import '../../modals/LoginModal/LoginModal.css';

// Passwordless staff sign-in for /employee: enter email → emailed login link,
// plus a 6-digit code for the iOS-PWA case (tapped links open in Safari, which
// can't see the installed app's session).
const EmployeeLogin = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [codeMode, setCodeMode] = useState(false);
  const [code, setCode] = useState('');

  useHeaderHeight();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await dispatch(sendEmployeeMagicLink(email.trim()));
      setSent(true);
    } catch (err) {
      console.error(err);
      setError('Could not send the login link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await dispatch(submitEmployeeMagicLinkCode(email.trim(), code.trim()));
    } catch (err) {
      setError(err.message || 'That code is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  if (codeMode) {
    return (
      <>
        <div className="login-backdrop" />
        <div className="login-modal">
          <p className="magic-link-heading">Enter your login code</p>
          <p className="magic-link-sub">
            Enter your email and the 6-digit code from your login email.
          </p>
          {error && <div className="error">{error}</div>}
          <form onSubmit={handleCodeSubmit}>
            <div className="form-group">
              <input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="form-group">
              <input
                className="code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Verifying…' : 'Log in with code'}
            </button>
          </form>
          <button
            type="button"
            className="magic-link-resend"
            onClick={() => { setCodeMode(false); setError(''); }}
            disabled={submitting}
          >
            Back to login link
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="login-backdrop" />
      <div className="login-modal">
        {!sent ? (
          <>
            <p className="magic-link-heading">Staff sign in</p>
            <p className="magic-link-sub">
              Enter your email and we'll send you a login link.
            </p>
            {error && <div className="error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send me a login link'}
              </button>
            </form>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => { setCodeMode(true); setError(''); }}
              disabled={submitting}
            >
              I have a code
            </button>
          </>
        ) : (
          <div className="magic-link-sent">
            <p className="magic-link-heading">Check your email</p>
            <p className="magic-link-sub">
              If that's a staff account, we sent a login link and a 6-digit code
              to <strong>{email}</strong>. It expires in 15 minutes — tap the
              link, or enter the code if you're on your phone.
            </p>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => { setCodeMode(true); setError(''); }}
              disabled={submitting}
            >
              Enter the code
            </button>
            <button
              type="button"
              className="magic-link-resend"
              onClick={() => setSent(false)}
              disabled={submitting}
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeLogin;
